// Multi-tenancy types. See ARCHITECTURE.md §17.

import { z } from "zod";

export type TenantId = string;
export type TenantSlug = string;
export type UserId = string;

export type TenancyMode = "shared" | "isolated";
export type TenantPlan = "free" | "pro" | "enterprise";
export type TenantStatus = "active" | "suspended" | "pending-deletion";

export interface Tenant {
  id: TenantId;
  slug: TenantSlug;
  name: string;
  plan: TenantPlan;
  status: TenantStatus;
  region: string;
  mode: TenancyMode;
  /** Display metadata (non-sensitive). */
  meta: {
    domain?: string;
    accent?: string;
    emoji?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: UserId;
  email: string;
  displayName?: string;
  /** Tenant memberships with role per tenant. */
  memberships: TenantMembership[];
  createdAt: string;
}

export type TenantRole = "owner" | "admin" | "editor" | "viewer";

export interface TenantMembership {
  tenantId: TenantId;
  role: TenantRole;
}

export interface JwtClaims {
  sub: UserId;
  email: string;
  /** Active tenant for this token. */
  tid: TenantId;
  /** Active tenant slug, denormalised. */
  tslug: TenantSlug;
  /** Active role for this token. */
  role: TenantRole;
  iat: number;
  exp: number;
  iss: string;
}

export interface TenantContext {
  tenant: Tenant;
  user: User;
  role: TenantRole;
  claims: JwtClaims;
}

export const zTenant: z.ZodType<Tenant> = z.object({
  id: z.string().uuid(),
  slug: z.string().regex(/^[a-z][a-z0-9-]{1,62}$/),
  name: z.string().min(1).max(120),
  plan: z.enum(["free", "pro", "enterprise"]),
  status: z.enum(["active", "suspended", "pending-deletion"]),
  region: z.string(),
  mode: z.enum(["shared", "isolated"]),
  meta: z.object({
    domain: z.string().optional(),
    accent: z.string().optional(),
    emoji: z.string().optional(),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const zJwtClaims: z.ZodType<JwtClaims> = z.object({
  sub: z.string(),
  email: z.string().email(),
  tid: z.string().uuid(),
  tslug: z.string(),
  role: z.enum(["owner", "admin", "editor", "viewer"]),
  iat: z.number(),
  exp: z.number(),
  iss: z.string(),
});

/**
 * Role hierarchy for permission checks. Higher index implies all lower powers.
 * Usage: `roleAtLeast(claims.role, "editor")`.
 */
export const ROLE_ORDER: TenantRole[] = ["viewer", "editor", "admin", "owner"];

export function roleAtLeast(have: TenantRole, need: TenantRole): boolean {
  return ROLE_ORDER.indexOf(have) >= ROLE_ORDER.indexOf(need);
}
